import type { OrgSettings } from '@kancelaria/shared';
import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { api, errorText, isOfflineQueued } from '../../api/client.js';
import { useData } from '../../store/data.js';
import { toast } from '../../store/ui.js';

/** Як зберегти сторінку — щоб при переході можна було запропонувати «Застосувати». */
export type DirtySave = (silent?: boolean) => Promise<boolean>;

interface Entry {
  dirty: boolean;
  busy: boolean;
  save: DirtySave;
  reset?: () => void;
}

/**
 * Реєстр незбережених змін.
 *
 * Зберігачів на екрані кілька: спільна чернетка налаштувань, назва курсу з
 * власним запитом і довідники, які пишуться окремо. Коли всі розділи стоять
 * одним сувоєм, вони живі одночасно, тож єдиного «поточного» зберігача не
 * буває — тримаємо всіх і питаємо кожного.
 *
 * Самі обробники лежать у звичайному об'єкті, а не в сховищі: вони народжуються
 * заново на кожному рендері, і в сховищі будили б підписників без потреби.
 */
let seq = 0;
const entries = new Map<number, Entry>();

export const useDirtyFlag = create<{ dirty: boolean; busy: boolean }>(() => ({
  dirty: false,
  busy: false,
}));

function recompute(): void {
  let dirty = false;
  let busy = false;
  for (const e of entries.values()) {
    dirty = dirty || e.dirty;
    busy = busy || e.busy;
  }
  const now = useDirtyFlag.getState();
  if (now.dirty !== dirty || now.busy !== busy) useDirtyFlag.setState({ dirty, busy });
}

/** Зберегти все незбережене. Перша ж невдача зупиняє решту. */
export const dirtySave = async (): Promise<boolean> => {
  for (const e of [...entries.values()]) {
    if (!e.dirty) continue;
    if (!(await e.save())) return false;
  }
  return true;
};

/** Повернути все до збереженого стану. */
export const dirtyReset = (): void => {
  for (const e of [...entries.values()]) e.reset?.();
};

/** Чи є що скасовувати — від цього залежить кнопка в панелі. */
export const canDirtyReset = (): boolean => [...entries.values()].some(e => e.dirty && e.reset);

/** Сторінка з власним збереженням (звання, установа) теж стає видимою для охорони переходу. */
export function useRegisterDirty(dirty: boolean, busy: boolean, save: DirtySave, reset?: () => void): void {
  const id = useRef(0);
  if (!id.current) id.current = ++seq;
  // Без списку залежностей: значення оновлюються щорендеру, а сховище
  // прокидається лише тоді, коли підсумок справді змінився.
  useEffect(() => {
    entries.set(id.current, { dirty, busy, save, reset });
    recompute();
  });
  useEffect(
    () => () => {
      entries.delete(id.current);
      recompute();
    },
    [],
  );
}

export interface SettingsDraft {
  draft: OrgSettings;
  update: (patch: Partial<OrgSettings>) => void;
  save: DirtySave;
  reset: () => void;
  dirty: boolean;
  busy: boolean;
}

/**
 * Чернетка налаштувань: сторінка правит копію, натискання «Зберегти» шле
 * повний об'єкт. Так неможливо зберегти половину суперечливої конфігурації.
 */
function useOwnSettingsDraft(autosave = false): SettingsDraft {
  const current = useData(s => s.session!.org.settings);
  const patchSettings = useData(s => s.patchSettings);
  const [draft, setDraft] = useState<OrgSettings>(() => structuredClone(current));
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(structuredClone(current)), [current]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(current);

  const save = useCallback(async (silent = false): Promise<boolean> => {
    setBusy(true);
    try {
      const res = await api.put<{ settings: OrgSettings }>('/api/settings', draft);
      patchSettings(res.settings);
      if (!silent) toast('Налаштування збережено', 'ok');
      return true;
    } catch (e) {
      if (isOfflineQueued(e)) {
        patchSettings(draft);
        toast('Збережено локально — надішлеться, коли з’явиться сервер', 'ok');
        return true;
      }
      toast(errorText(e), 'err');
      return false;
    } finally {
      setBusy(false);
    }
  }, [draft, patchSettings]);

  const reset = useCallback(() => setDraft(structuredClone(current)), [current]);

  useRegisterDirty(dirty, busy, () => save(), reset);

  // Екрани без кнопки «Зберегти»: правка йде на сервер сама, трохи згодом,
  // щоб серію змін не слати по одній. Невдача лишає чернетку «брудною» —
  // охорона переходу про неї нагадає.
  const failed = useRef(false);
  useEffect(() => {
    if (!autosave || !dirty || busy || failed.current) return;
    const timer = setTimeout(() => {
      void save(true).then(ok => {
        failed.current = !ok;
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [autosave, dirty, busy, draft, save]);
  useEffect(() => {
    failed.current = false;
  }, [draft]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty && !busy) void save();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dirty, busy, save]);

  return {
    draft,
    busy,
    dirty,
    update: patch => setDraft(d => ({ ...d, ...patch })),
    reset,
    save,
  };
}

const DraftContext = createContext<SettingsDraft | null>(null);

/**
 * Одна чернетка на весь розділ налаштувань.
 *
 * Позначки, шаблони, наряди й документи правлять той самий об'єкт налаштувань.
 * Поки вони були окремими сторінками, кожна тримала власну копію — і це
 * сходило з рук, бо жила завжди одна. Одним сувоєм копії затирали б одна одну:
 * збереження позначок відіслало б і сторі наряди разом з ними.
 */
export function SettingsDraftProvider({ children }: { children: ReactNode }): ReactNode {
  const value = useOwnSettingsDraft();
  return createElement(DraftContext.Provider, { value }, children);
}

/** Чернетка розділу. Поза «Налаштуваннями» (рапорти) заводить власну. */
export function useSettingsDraft(): SettingsDraft {
  const shared = useContext(DraftContext);
  if (!shared) throw new Error('useSettingsDraft: немає SettingsDraftProvider');
  return shared;
}

/** Для екранів поза налаштуваннями: рапорти правлять ті самі налаштування самі. */
export const useStandaloneSettingsDraft = (): SettingsDraft => useOwnSettingsDraft(true);

/** Панель збереження, однакова на всіх сторінках налаштувань. */
export interface SaveBarProps {
  dirty: boolean;
  busy: boolean;
  onSave: () => void;
  onReset: () => void;
  label?: string;
}
